import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type DrawingMode = 'Dibujar' | 'Editar' | 'Eliminar' | 'Consulta' | 'Copiar' | 'Seleccionar';

// Interfaz para un dibujo individual
export interface DrawingFeature {
  id: string;
  type: string;
  geometry: any; // GeoJSON geometry
  properties: {
    finca?: string;
    computo?: number;
    [key: string]: any;
  };
  storeyId: string;
  createdAt: string;
  updatedAt: string;
}

// Interfaz para los dibujos de una capa
export interface StoreyDrawings {
  [storeyId: string]: DrawingFeature[];
}

export interface DrawingState {
  // Estado del servicio
  isInitialized: boolean;
  isReady: boolean;
  
  // Modo actual
  currentMode: DrawingMode;
  previousMode: DrawingMode | null;
  
  // Herramientas activas
  activeTools: {
    draw: boolean;
    edit: boolean;
    delete: boolean;
    query: boolean;
    copy: boolean;
    select: boolean;
  };
  
  // Estado de dibujo
  isDrawing: boolean;
  currentFeature: any | null;
  selectedFeatures: any[];
  
  // Dibujos almacenados por capa
  drawings: StoreyDrawings;
  
  // Operaciones
  isLoading: boolean;
  error: string | null;
  
  // Acciones
  setInitialized: (initialized: boolean) => void;
  setReady: (ready: boolean) => void;
  setMode: (mode: DrawingMode) => void;
  setPreviousMode: (mode: DrawingMode | null) => void;
  toggleTool: (toolName: keyof DrawingState['activeTools']) => void;
  setDrawing: (drawing: boolean) => void;
  setCurrentFeature: (feature: any | null) => void;
  setSelectedFeatures: (features: any[]) => void;
  addSelectedFeature: (feature: any) => void;
  removeSelectedFeature: (feature: any) => void;
  clearSelectedFeatures: () => void;
  
  // Acciones para dibujos
  addDrawing: (storeyId: string, feature: DrawingFeature) => void;
  updateDrawing: (storeyId: string, featureId: string, updates: Partial<DrawingFeature>) => void;
  removeDrawing: (storeyId: string, featureId: string) => void;
  setStoreyDrawings: (storeyId: string, drawings: DrawingFeature[]) => void;
  clearStoreyDrawings: (storeyId: string) => void;
  clearAllDrawings: () => void;
  getStoreyDrawings: (storeyId: string) => DrawingFeature[];
  getAllDrawings: () => StoreyDrawings;
  
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

// Función de validación para dibujos
const validateDrawing = (feature: DrawingFeature): boolean => {
  // Validar que tenga ID
  if (!feature.id || typeof feature.id !== 'string') {
    console.warn('❌ DrawingStore: Feature sin ID válido');
    return false;
  }
  
  // Validar que tenga tipo
  if (!feature.type || typeof feature.type !== 'string') {
    console.warn('❌ DrawingStore: Feature sin tipo válido');
    return false;
  }
  
  // Validar que tenga geometría
  if (!feature.geometry) {
    console.warn('❌ DrawingStore: Feature sin geometría');
    return false;
  }
  
  // La geometría puede ser un array (formato interno) o un objeto GeoJSON (formato importado)
  const hasValidGeometry = Array.isArray(feature.geometry) || 
    (typeof feature.geometry === 'object' && feature.geometry.type && feature.geometry.coordinates);
  
  if (!hasValidGeometry) {
    console.warn('❌ DrawingStore: Feature sin geometría válida');
    return false;
  }
  
  // Validar que tenga storeyId
  if (!feature.storeyId || typeof feature.storeyId !== 'string') {
    console.warn('❌ DrawingStore: Feature sin storeyId válido');
    return false;
  }
  
  // Validar timestamps
  if (!feature.createdAt || !feature.updatedAt) {
    console.warn('❌ DrawingStore: Feature sin timestamps válidos');
    return false;
  }
  
  return true;
};

const initialState = {
  isInitialized: false,
  isReady: false,
  currentMode: 'Dibujar' as DrawingMode,
  previousMode: null as DrawingMode | null,
  activeTools: {
    draw: true,
    edit: false,
    delete: false,
    query: false,
    copy: false,
    select: false,
  },
  isDrawing: false,
  currentFeature: null,
  selectedFeatures: [],
  drawings: {} as StoreyDrawings,
  isLoading: false,
  error: null,
};

export const useDrawingStore = create<DrawingState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
      
        setInitialized: (initialized) => set({ isInitialized: initialized }),
        setReady: (ready) => set({ isReady: ready }),
        
        setMode: (mode) => set((state) => ({
          currentMode: mode,
          previousMode: state.currentMode,
          activeTools: {
            draw: mode === 'Dibujar',
            edit: mode === 'Editar',
            delete: mode === 'Eliminar',
            query: mode === 'Consulta',
            copy: mode === 'Copiar',
            select: mode === 'Seleccionar',
          },
        })),
        
        setPreviousMode: (mode) => set({ previousMode: mode }),
        
        toggleTool: (toolName) => set((state) => ({
          activeTools: {
            ...state.activeTools,
            [toolName]: !state.activeTools[toolName],
          },
        })),
        
        setDrawing: (drawing) => set({ isDrawing: drawing }),
        setCurrentFeature: (feature) => set({ currentFeature: feature }),
        
        setSelectedFeatures: (features) => set({ selectedFeatures: features }),
        addSelectedFeature: (feature) => set((state) => ({
          selectedFeatures: [...state.selectedFeatures, feature],
        })),
        removeSelectedFeature: (feature) => set((state) => ({
          selectedFeatures: state.selectedFeatures.filter(f => f !== feature),
        })),
        clearSelectedFeatures: () => set({ selectedFeatures: [] }),
        
        // Acciones para dibujos
        addDrawing: (storeyId, feature) => set((state) => {
          // Validar el dibujo antes de guardar
          if (!validateDrawing(feature)) {
            console.warn('⚠️ DrawingStore: Dibujo no válido, no se guardará:', feature);
            return state;
          }
          
          const currentDrawings = state.drawings[storeyId] || [];
          
          return {
            drawings: {
              ...state.drawings,
              [storeyId]: [...currentDrawings, feature],
            }
          };
        }),
        
        updateDrawing: (storeyId, featureId, updates) => set((state) => {
          const currentDrawings = state.drawings[storeyId] || [];
          const updatedDrawings = currentDrawings.map(drawing => 
            drawing.id === featureId 
              ? { ...drawing, ...updates, updatedAt: new Date().toISOString() }
              : drawing
          );
          
          return {
            drawings: {
              ...state.drawings,
              [storeyId]: updatedDrawings,
            }
          };
        }),
        
        removeDrawing: (storeyId, featureId) => set((state) => {
          const currentDrawings = state.drawings[storeyId] || [];
          const filteredDrawings = currentDrawings.filter(drawing => drawing.id !== featureId);
          
          return {
            drawings: {
              ...state.drawings,
              [storeyId]: filteredDrawings,
            }
          };
        }),
        
        setStoreyDrawings: (storeyId, drawings) => set((state) => {
          return {
            drawings: {
              ...state.drawings,
              [storeyId]: drawings,
            }
          };
        }),
        
        clearStoreyDrawings: (storeyId) => set((state) => {
          const newDrawings = { ...state.drawings };
          delete newDrawings[storeyId];
          
          return {
            drawings: newDrawings
          };
        }),
        
        clearAllDrawings: () => set(() => {
          return {
            drawings: {}
          };
        }),
        
        getStoreyDrawings: (storeyId) => {
          const state = get();
          return state.drawings[storeyId] || [];
        },
        
        getAllDrawings: () => {
          const state = get();
          return state.drawings;
        },
        
        setLoading: (loading) => set({ isLoading: loading }),
        setError: (error) => set({ error }),
        
        reset: () => set(initialState),
      }),
      {
        name: 'drawing-store-persist',
        partialize: (state) => ({
          drawings: state.drawings,
        }),
      }
    )
  )
);
